import React, { useState, useEffect, useRef } from 'react';

interface SegmentedProgressProps {
  segments?: number;
  duration?: number;
  onComplete?: () => void;
  className?: string;
  filledClassName?: string;
  emptyClassName?: string;
}

export const SegmentedProgress: React.FC<SegmentedProgressProps> = ({
  segments = 16,
  duration = 4000,
  onComplete,
  className,
  filledClassName,
  emptyClassName,
}) => {
  const [filledSegments, setFilledSegments] = useState(1);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completeCalled = useRef(false);

  useEffect(() => {
    completeCalled.current = false;
    const timePerSegment = duration / segments;
    let currentSegment = 1;

    intervalRef.current = setInterval(() => {
      currentSegment++;
      setFilledSegments(currentSegment);

      if (currentSegment >= segments) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (!completeCalled.current && onComplete) {
          completeCalled.current = true;
          onComplete();
        }
      }
    }, timePerSegment);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [segments, duration, onComplete]);

  return (
    <div className={`flex flex-col gap-2 ${className || ''}`}>
      <div
        className="flex gap-[3px] py-1"
        role="progressbar"
        aria-valuenow={filledSegments}
        aria-valuemin={0}
        aria-valuemax={segments}
      >
        {Array.from({ length: segments }).map((_, index) => {
          const isFilled = index < filledSegments;
          return (
            <div
              key={index}
              className={`h-2 flex-1 rounded-[3px] origin-center transition-all duration-300 ease-linear ${
                isFilled
                  ? filledClassName || 'bg-gradient-to-r from-blue-400 to-primary shadow-[0_0_8px_rgba(67,97,238,0.5)]'
                  : emptyClassName || 'bg-gray-200'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};
